/**
 * Orders listings so a building's for-sale (could_sell) entries lead, with the
 * rest (buyers) following. Returns a new array; the input is left untouched.
 */
export const sortListingsBySellerFirst = <T extends { could_sell?: boolean }>(
  listings: T[],
): T[] =>
  [...listings].sort((a, b) => {
    const av = a.could_sell ? 0 : 1;
    const bv = b.could_sell ? 0 : 1;
    return av - bv;
  });

type CreatedListing = {
  _id?: string;
  area_interest_id?: string;
  created_at?: string | null;
  could_sell?: boolean;
};

const creationTime = (listing: CreatedListing): number => {
  // MongoDB stores UTC times, but area interests are sent as ISO strings
  // without an offset. Date.parse treats those as browser-local time.
  const raw = listing.created_at;
  const utcDate =
    raw && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(raw)
      ? `${raw}Z`
      : raw;
  const created = utcDate ? Date.parse(utcDate) : NaN;
  if (Number.isFinite(created)) return created;

  // Match the map API's legacy fallback: ObjectId creation time, never the
  // last edit time. Rows without either date sort last and retain their order.
  const id = listing._id ?? listing.area_interest_id ?? '';
  return /^[a-f\d]{24}$/i.test(id) ? parseInt(id.slice(0, 8), 16) * 1000 : 0;
};

/** Sellers first, newest-created first within each seller/buyer group. */
export const sortPreviewListings = <T extends CreatedListing>(
  listings: T[],
): T[] =>
  [...listings].sort((a, b) => {
    if (Boolean(a.could_sell) !== Boolean(b.could_sell)) {
      return a.could_sell ? -1 : 1;
    }
    return creationTime(b) - creationTime(a);
  });
